import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
} from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { RadioCardGroupComponent } from './radio-card-group.component';

@Component({
  selector: 'ct-lib-radio-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './radio-card.component.html',
  styleUrls: ['./radio-card.component.scss'],
  imports: [FaIconComponent],
  host: {
    '[class.selected]': 'isSelected()',
    '[class.disabled]': 'isDisabled()',
  },
})
export class RadioCardComponent implements OnInit {
  private readonly group = inject(RadioCardGroupComponent);
  private readonly destroyRef = inject(DestroyRef);

  readonly value = input.required<unknown>();
  readonly icon = input<IconProp | null>(null);
  readonly label = input.required<string>();
  readonly price = input<string | null>(null);
  readonly priceHint = input<string | null>(null);
  readonly sublabel = input<string | null>(null);
  readonly description = input<string | null>(null);
  readonly disabled = input(false);

  readonly isSelected = computed(() =>
    this.group.isValueSelected(this.value()),
  );
  readonly isDisabled = computed(
    () => this.group.isDisabled() || this.disabled(),
  );
  readonly isMultiple = computed(() => this.group.multiselect());
  readonly inputName = computed(() => this.group.name());

  constructor() {
    effect(() => {
      this.value();
      this.isDisabled();
      this.group.syncSelectionState();
    });
  }

  ngOnInit(): void {
    const unregister = this.group.registerItem({
      value: () => this.value(),
      disabled: () => this.isDisabled(),
    });

    this.destroyRef.onDestroy(unregister);
  }

  onSelect(): void {
    this.group.select(this.value());
  }

  /**
   * Selects the card when clicking projected content outside the label.
   * Ignores interactive controls such as nested buttons.
   * @param {MouseEvent} event Card click event
   * @returns {void}
   */
  onCardClick(event: MouseEvent): void {
    if (this.isDisabled()) {
      return;
    }
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    if (target.closest('button, a, input, textarea, select')) {
      return;
    }
    if (this.isMultiple() && target.closest('label')) {
      return;
    }
    this.onSelect();
  }

  /**
   * Toggles a checkbox card from the label without native double-toggles.
   * @param {MouseEvent} event Label click event
   * @returns {void}
   */
  onLabelClick(event: MouseEvent): void {
    if (!this.isMultiple() || this.isDisabled()) {
      return;
    }
    event.preventDefault();
    const target = event.target;
    if (target instanceof Element && target.closest('input')) {
      return;
    }
    this.onSelect();
  }

  /**
   * Stops checkbox clicks from bubbling to the card.
   * @param {MouseEvent} event Input click event
   * @returns {void}
   */
  onInputClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  onBlur(): void {
    this.group.markTouched();
  }
}
